import { Component, inject, computed } from '@angular/core';
import { ChildrenOutletContexts, Router, RouterModule, NavigationEnd } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map } from 'rxjs';
import { routeFadeAnimation } from './core/animations/route-animations';
import {
  RdsHeaderComponent,
  RdsHeaderAppNameDirective,
  RdsHeaderActionsDirective,
  RdsHeaderNavigationPrimaryDirective,
  RdsIconComponent,
  RdsIconButtonComponent,
  RdsTextButtonComponent,
  RdsFooterModule,
  RdsDialogService,
} from '@rds/angular-components';
import { AuthService } from './core/services/auth.service';
import { LegalDialogComponent } from './core/components/legal-dialog.component';
import {
  TERMS_OF_USE_HTML,
  PRIVACY_NOTICE_HTML,
  SUPPORT_HTML,
} from './core/constants/legal-content';

interface NavItem {
  label: string;
  routerLink: string;
}

@Component({
  imports: [
    RouterModule,
    RdsHeaderComponent,
    RdsHeaderAppNameDirective,
    RdsHeaderActionsDirective,
    RdsHeaderNavigationPrimaryDirective,
    RdsIconComponent,
    RdsIconButtonComponent,
    RdsTextButtonComponent,
    RdsFooterModule,
  ],
  selector: 'app-root',
  templateUrl: './app.html',
  styleUrl: './app.scss',
  animations: [routeFadeAnimation],
})
export class App {
  readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly contexts = inject(ChildrenOutletContexts);
  private readonly dialog = inject(RdsDialogService);

  readonly primaryNavigation = computed<NavItem[]>(() =>
    this.authService.user()?.isPdmaMember === false
      ? [{ label: 'Home', routerLink: '/' }]
      : [
          { label: 'Home', routerLink: '/' },
          { label: 'Results', routerLink: '/results' },
        ],
  );

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((e) => e instanceof NavigationEnd),
      map((e) => (e as NavigationEnd).urlAfterRedirects),
    ),
    { initialValue: this.router.url },
  );

  /** Hide the app shell (header + content wrapper) on login and auth callback pages */
  readonly showAppShell = computed(() => {
    const url = this.currentUrl();
    return !url.startsWith('/login') && !url.startsWith('/auth/callback');
  });

  /** Hide primary navigation when the user hasn't selected a team yet */
  readonly showPrimaryNav = computed(() => {
    const url = this.currentUrl();
    return this.authService.isAuthenticated() && !url.startsWith('/select-team');
  });

  getRouteAnimationData() {
    return this.contexts.getContext('primary')?.route?.snapshot?.data?.['animation'];
  }

  goHome(): void {
    this.router.navigate(['/']);
  }

  openTermsOfUse(): void {
    this.dialog.open(LegalDialogComponent, {
      size: 'l',
      data: { title: 'Terms of Use', htmlContent: TERMS_OF_USE_HTML },
    });
  }

  openPrivacyNotice(): void {
    this.dialog.open(LegalDialogComponent, {
      size: 'l',
      data: { title: 'Privacy Notice', htmlContent: PRIVACY_NOTICE_HTML },
    });
  }

  openSupport(): void {
    this.dialog.open(LegalDialogComponent, {
      size: 'l',
      data: { title: 'Support', htmlContent: SUPPORT_HTML },
    });
  }
}
